// Drawer showing the recent hit log for a webhook. One row per POST,
// each expandable to reveal the truncated payload preview, headers,
// and the linked ingest job id.

import { Dialog } from "@base-ui-components/react/dialog";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, ChevronRight, XCircle, X } from "lucide-react";
import { useState } from "react";

interface DrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	projectId: string;
	webhookId: string;
}

interface WebhookEvent {
	id: string;
	receivedAt: string;
	sourceIp: string | null;
	status: "ok" | "rejected" | "error";
	responseCode: number;
	error: string | null;
	payloadSize: number;
	payloadPreview: string | null;
	payloadTruncated: boolean;
	itemsIngested: number;
	jobId: string | null;
}

async function fetchEvents(projectId: string, webhookId: string): Promise<WebhookEvent[]> {
	const r = await fetch(
		`/api/projects/${projectId}/webhooks/${webhookId}/events?limit=100`,
		{ credentials: "include" },
	);
	if (!r.ok) throw new Error(`events: ${r.status}`);
	return ((await r.json()) as { events: WebhookEvent[] }).events;
}

export function WebhookEventsDrawer({
	open,
	onOpenChange,
	projectId,
	webhookId,
}: DrawerProps) {
	const eventsQ = useQuery({
		queryKey: ["webhook-events", projectId, webhookId],
		queryFn: () => fetchEvents(projectId, webhookId),
		enabled: open,
		refetchInterval: open ? 3000 : false,
	});

	const events = eventsQ.data ?? [];
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
				<Dialog.Popup
					className="fixed right-0 top-0 bottom-0 z-50 w-[520px] max-w-[calc(100vw-32px)] flex flex-col"
					style={{
						background: "rgb(20 20 23 / 0.98)",
						backdropFilter: "blur(20px) saturate(180%)",
						WebkitBackdropFilter: "blur(20px) saturate(180%)",
						borderLeft: "1px solid rgb(255 255 255 / 0.1)",
						boxShadow: "-24px 0 60px rgba(0,0,0,0.6)",
					}}
				>
					<div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
						<Dialog.Title className="text-[14px] font-medium">
							Webhook events
						</Dialog.Title>
						<Dialog.Close className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors">
							<X className="w-4 h-4" />
						</Dialog.Close>
					</div>
					<div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
						{events.length === 0 && !eventsQ.isLoading && (
							<div className="text-[13px] text-[var(--color-text-muted)] text-center py-6">
								No events yet. POST to the webhook URL to see hits here.
							</div>
						)}
						{events.map((e) => {
							const isOpen = expanded.has(e.id);
							return (
								<div
									key={e.id}
									className="glass rounded-[10px] overflow-hidden"
								>
									<button
										type="button"
										onClick={() => {
											const next = new Set(expanded);
											if (next.has(e.id)) next.delete(e.id);
											else next.add(e.id);
											setExpanded(next);
										}}
										className="w-full flex items-center gap-2 px-3 py-2 text-left"
									>
										<StatusIcon status={e.status} />
										<div className="flex-1 min-w-0">
											<div className="flex items-baseline gap-2 text-[12px]">
												<span className="mono">{e.responseCode}</span>
												<span className="text-[var(--color-text-dim)]">·</span>
												<span className="text-[var(--color-text-muted)]">
													{relativeTime(e.receivedAt)}
												</span>
												{e.itemsIngested > 0 && (
													<>
														<span className="text-[var(--color-text-dim)]">·</span>
														<span>{e.itemsIngested} ingested</span>
													</>
												)}
												<span className="text-[var(--color-text-dim)]">·</span>
												<span className="text-[var(--color-text-dim)]">
													{formatBytes(e.payloadSize)}
												</span>
											</div>
											{e.error && (
												<div className="text-[11px] text-[var(--color-danger)] truncate mt-0.5">
													{e.error}
												</div>
											)}
										</div>
										{isOpen ? (
											<ChevronDown className="w-4 h-4 text-[var(--color-text-dim)]" />
										) : (
											<ChevronRight className="w-4 h-4 text-[var(--color-text-dim)]" />
										)}
									</button>
									{isOpen && (
										<div className="border-t border-[var(--color-border)] px-3 py-2 bg-black/30 text-[11px] space-y-2">
											<KV label="received_at" value={new Date(e.receivedAt).toLocaleString()} />
											<KV label="status" value={e.status} />
											{e.sourceIp && <KV label="source_ip" value={e.sourceIp} mono />}
											{e.jobId && <KV label="job_id" value={e.jobId} mono />}
											{e.payloadPreview && (
												<div>
													<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
														payload
														{e.payloadTruncated && (
															<span className="ml-1 text-[var(--color-text-muted)]">
																(truncated to 8 KB)
															</span>
														)}
													</div>
													<pre className="mono text-[10.5px] text-[var(--color-text-muted)] whitespace-pre-wrap break-words max-h-[280px] overflow-y-auto bg-black/40 rounded-md p-2 border border-[var(--color-border)]">
														{e.payloadPreview}
													</pre>
												</div>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function StatusIcon({ status }: { status: WebhookEvent["status"] }) {
	switch (status) {
		case "ok":
			return <CheckCircle2 className="w-4 h-4 text-[var(--color-success)]" />;
		case "rejected":
			return <XCircle className="w-4 h-4 text-[var(--color-text-dim)]" />;
		case "error":
			return <XCircle className="w-4 h-4 text-[var(--color-danger)]" />;
	}
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
	return (
		<div>
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
				{label}
			</div>
			<div className={`text-[12px] truncate ${mono ? "mono" : ""}`}>{value}</div>
		</div>
	);
}

function relativeTime(iso: string): string {
	const d = new Date(iso);
	const diff = Date.now() - d.getTime();
	if (diff < 0) return "just now";
	const s = Math.round(diff / 1000);
	if (s < 60) return `${s}s ago`;
	const m = Math.round(s / 60);
	if (m < 60) return `${m}m ago`;
	const h = Math.round(m / 60);
	if (h < 24) return `${h}h ago`;
	const days = Math.round(h / 24);
	return `${days}d ago`;
}

function formatBytes(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
